import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Flex,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
  Spinner,
  Text,
  Center,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormHelperText,
} from "@chakra-ui/react";
import { cqAPI } from "../../lib/Auth";

export default function PhonePricingTable() {
  const [products, setProducts] = useState([]);
  const [qty, setQty] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Promo state
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState(null);
  const [promo, setPromo] = useState(null);

  /* Fetch products */
  useEffect(() => {
    cqAPI
      .get("/phone/order")
      .then((json) => {
        if (json?.type === "ok" && Array.isArray(json.data)) {
          const mapped = json.data.map((p) => ({
            id: p.id,
            priceId: p.default_price.id,
            name: p.name,
            description: p.description,
            image: p.image,
            priceCents: Number(p.default_price.unit_amount_decimal),
          }));
          setProducts(mapped);
        } else {
          throw new Error(json?.error || "Unexpected response format");
        }
      })
      .catch((err) => {
        console.error("/phone/order error:", err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  /* Helpers */
  const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

  const subtotal = useMemo(
    () =>
      products.reduce((sum, p) => sum + (qty[p.id] || 0) * p.priceCents, 0),
    [products, qty]
  );

  const selectedItems = useMemo(
    () =>
      products
        .filter((p) => (qty[p.id] || 0) > 0)
        .map((p) => ({
          id: p.id,
          priceId: p.priceId,
          name: p.name,
          count: qty[p.id],
          subtotal: (qty[p.id] || 0) * p.priceCents,
        })),
    [products, qty]
  );

  // Subtotal eligible for promo (if coupon applies to specific product names)
  const eligibleSubtotal = useMemo(() => {
    if (!promo || !promo?.products?.length) return subtotal;
    const appliesTo = new Set((promo.products || []).map((n) => String(n)));
    return selectedItems.reduce(
      (sum, it) => (appliesTo.has(it.name) ? sum + it.subtotal : sum),
      0
    );
  }, [promo, subtotal, selectedItems]);

  // Discount calculation (amount_off takes precedence if present)
  const discountCents = useMemo(() => {
    if (!promo) return 0;
    const amountOff = promo.amount_off || 0;
    const percentOff = promo.percent_off || 0;
    const percentDiscount = Math.round(eligibleSubtotal * (percentOff / 100));
    const rawDiscount = amountOff ? amountOff : percentDiscount;
    const maxDiscount = Math.min(rawDiscount, eligibleSubtotal);
    return Math.max(0, maxDiscount);
  }, [promo, eligibleSubtotal]);

  const finalTotal = Math.max(0, subtotal - discountCents);

  const handleQtyChange = (productId, value) => {
    const num = Number(value);
    setQty((prev) => ({ ...prev, [productId]: num < 0 ? 0 : num }));
  };

  const handleAdd = (productId) => {
    setQty((prev) => ({ ...prev, [productId]: (prev[productId] || 0) + 1 }));
  };

  const handleCheckout = () => {
    if (selectedItems.length === 0) {
      toast({
        status: "info",
        description: "Please add at least one item first",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    onOpen();
  };

  // Validate promo
  const applyPromo = async () => {
    if (!promoCode?.trim()) {
      setPromoError("Please enter a promotion code.");
      setPromo(null);
      return;
    }
    setPromoError(null);
    setPromoLoading(true);
    try {
      const res = await cqAPI.get(
        `/verify-promo?promotion=${encodeURIComponent(promoCode.trim())}`
      );
      if (res?.type === "ok" && res?.data) {
        setPromo(res.data);
        setPromoError(null);
      } else {
        setPromo(null);
        setPromoError(res?.msg || "This is not a valid promotion.");
      }
    } catch (e) {
      console.error("verify-promo error:", e);
      setPromo(null);
      setPromoError(e?.message || "Failed to validate promotion.");
    } finally {
      setPromoLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (submitting || promoLoading) return;
    setSubmitting(true);
    try {
      const payload = {
        items: selectedItems.map((it) => ({
          product_id: it.id,
          price_id: it.priceId,
          count: it.count,
        })),
        ...(promo ? { promotion_id: promo.id } : {})
      };

      const response = await cqAPI.post("/phone/order/pay", payload);

      if (response.type === "ok") {
        toast({ status: "success", description: "Payment successful!" });
        setQty({});
        setPromo(null);
        setPromoCode("");
        setPromoError(null);
        onClose();
        return;
      }
      if (response.msg) {
        toast({ status: "error", description: response.msg });
      }
    } catch (err) {
      console.error("/phone/order/pay error:", err);
      toast({ status: "error", description: err.message || "Payment failed" });
    } finally {
      setSubmitting(false);
    }
  };

  /* Render */
  if (loading) {
    return (
      <Center w="full" minH="60vh" flexDir="column" py={10}>
        <Spinner size="lg" />
        <Text mt={4}>Loading pricing…</Text>
      </Center>
    );
  }

  if (error) {
    return (
      <Center w="full" minH="60vh" flexDir="column" py={10}>
        <Text color="red.500">{error}</Text>
      </Center>
    );
  }

  return (
    <Box w="full" maxW="6xl" mx="auto" px={[4, 6, 8]} py={10}>
      <Table variant="simple" size="md">
        <Thead>
          <Tr>
            <Th>Product</Th>
            <Th>Description</Th>
            <Th isNumeric>Price</Th>
            <Th isNumeric>Qty</Th>
            <Th></Th>
          </Tr>
        </Thead>
        <Tbody>
          {products.map((p) => (
            <Tr key={p.id}>
              <Td>
                <Flex align="center" gap={4}>
                  {p.image ? (
                    <Image
                      src={p.image}
                      alt={p.name}
                      boxSize="60px"
                      objectFit="cover"
                      borderRadius="md"
                    />
                  ) : null}
                  <Text fontWeight="medium">{p.name}</Text>
                </Flex>
              </Td>
              <Td maxW="400px">
                <Text noOfLines={2}>{p.description}</Text>
              </Td>
              <Td isNumeric>{formatPrice(p.priceCents)}</Td>
              <Td isNumeric>
                <NumberInput
                  size="sm"
                  maxW="80px"
                  min={0}
                  value={qty[p.id] || 0}
                  onChange={(v) => handleQtyChange(p.id, v)}
                  isDisabled={!qty[p.id]}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Td>
              <Td>
                <Button size="sm" onClick={() => handleAdd(p.id)}>
                  Add
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Flex justify="flex-end" mt={8} gap={4}>
        <Text fontWeight="bold" fontSize="lg">
          Total: {formatPrice(subtotal)}
        </Text>
        <Button colorScheme="blue" onClick={handleCheckout} isDisabled={subtotal === 0}>
          Checkout
        </Button>
      </Flex>

      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Your Order</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedItems.map((it) => (
              <Flex key={it.id} justify="space-between" my={2}>
                <Text>
                  {it.name} × {it.count}
                </Text>
                <Text>{formatPrice(it.subtotal)}</Text>
              </Flex>
            ))}

            <Flex justify="space-between" mt={4} pt={2} borderTop="1px solid" borderColor="gray.200">
              <Text fontWeight="bold">Subtotal</Text>
              <Text fontWeight="bold">{formatPrice(subtotal)}</Text>
            </Flex>

            {/* Promo code */}
            <FormControl mt={4}>
              <InputGroup>
                <Input
                  placeholder="Promotion code (optional)"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applyPromo();
                  }}
                />
                <InputRightElement width="5.5rem">
                  <Button h="1.75rem" size="sm" onClick={applyPromo} isLoading={promoLoading}>
                    Apply
                  </Button>
                </InputRightElement>
              </InputGroup>
              {promoError ? (
                <FormHelperText color="red.500">{promoError}</FormHelperText>
              ) : promo ? (
                <FormHelperText color="green.600">
                  {promo.name ? `${promo.name} — ` : ""}
                  {promo.percent_off
                    ? `${promo.percent_off}% off`
                    : promo.amount_off
                    ? `${formatPrice(promo.amount_off)} off`
                    : "Promotion applied"}
                  {promo.products?.length ? ` (applies to: ${promo.products.join(", ")})` : ""}
                </FormHelperText>
              ) : (
                <FormHelperText>Enter a code and press Apply.</FormHelperText>
              )}
            </FormControl>

            {promo && discountCents > 0 ? (
              <>
                <Flex justify="space-between" mt={3}>
                  <Text>Discount</Text>
                  <Text>-{formatPrice(discountCents)}</Text>
                </Flex>
                <Flex justify="space-between" mt={3} pt={2} borderTop="1px dashed" borderColor="gray.200">
                  <Text fontWeight="bold">Total</Text>
                  <Text fontWeight="bold">{formatPrice(finalTotal)}</Text>
                </Flex>
              </>
            ) : null}
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              isDisabled={submitting || promoLoading}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirm}
              isLoading={submitting || promoLoading}  // spinner while validating or paying
              isDisabled={submitting || promoLoading}
            >
              Confirm &amp; Pay
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
